import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Plus, Edit, Save, Trash2, FileText, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ImageInput from "../components/ImageInput";

const EMPTY = { title: "", slug: "", excerpt: "", content_html: "", cover_url: "", author_name: "Equipe MorarBem", tags: [], published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagsStr, setTagsStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/blog?page=1&page_size=50").then((r) => setPosts(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm(EMPTY); setTagsStr(""); setEditing("NEW"); };
  const openEdit = async (slug) => {
    const { data } = await api.get(`/blog/${slug}`);
    setForm({...EMPTY, ...data});
    setTagsStr((data.tags || []).join(", "));
    setEditing(slug);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {...form, tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean)};
      if (editing === "NEW") await api.post("/blog", payload);
      else await api.patch(`/blog/${editing}`, payload);
      toast.success("Post salvo");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Excluir este post?")) return;
    try { await api.delete(`/blog/${slug}`); toast.success("Excluído"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"><ArrowLeft size={14}/> Voltar</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><FileText size={28}/> Blog</h1>
          <p className="text-stone-500 text-sm mt-1">Artigos, novidades e dicas. Aparecem em /blog.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="blog-new"><Plus size={14} className="mr-1.5"/>Novo post</Button>
      </div>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        {loading ? <p className="p-10 text-center text-stone-400">Carregando...</p> : posts.length === 0 ?
          <p className="p-10 text-center text-stone-500">Nenhum post ainda.</p> : (
          <div className="divide-y divide-stone-100">
            {posts.map((p) => (
              <div key={p.slug} className="p-4 flex items-center gap-3" data-testid={`blog-row-${p.slug}`}>
                <div className="w-20 aspect-video bg-stone-100 rounded-lg overflow-hidden shrink-0">
                  {p.cover_url ? <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-stone-300"><FileText size={20}/></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-stone-900 truncate">{p.title}</h3>
                    {p.published ? <Eye size={14} className="text-emerald-600 shrink-0"/> : <EyeOff size={14} className="text-stone-400 shrink-0"/>}
                    {p.tags?.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-bold uppercase">{t}</span>)}
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{p.excerpt}</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">{p.views || 0} views · {p.author_name}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p.slug)} className="rounded-lg" data-testid={`blog-edit-${p.slug}`}><Edit size={12}/></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.slug)} className="text-red-600 rounded-lg" data-testid={`blog-del-${p.slug}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "NEW" ? "Novo post" : "Editar post"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div className="sm:col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" data-testid="blog-form-title"/></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="auto-gerado" className="rounded-xl mt-1 font-mono text-sm"/></div>
              <div><Label>Autor</Label><Input value={form.author_name} onChange={(e) => setForm({...form, author_name: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2"><Label>Resumo (excerpt)</Label><Textarea value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} rows={2} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2"><Label>Imagem de capa</Label><div className="mt-1"><ImageInput value={form.cover_url} onChange={(v) => setForm({...form, cover_url: v})} aspect="16/9" testid="blog-cover"/></div></div>
              <div className="sm:col-span-2">
                <Label>Conteúdo (HTML aceito — &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;img src=&quot;…&quot;/&gt;, etc)</Label>
                <Textarea value={form.content_html} onChange={(e) => setForm({...form, content_html: e.target.value})} rows={14} className="rounded-xl mt-1 font-mono text-xs" data-testid="blog-form-content"/>
              </div>
              <div className="sm:col-span-2"><Label>Tags (separadas por vírgula)</Label><Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="cashback, dicas, novidades" className="rounded-xl mt-1"/></div>
              <div className="flex items-center gap-2 mt-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({...form, published: v})} data-testid="blog-published"/><Label>Publicado</Label></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={busy || !form.title} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="blog-save">
                <Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
